/**
 * The two gateway reads the Connect wizard and the gateway panels share: the
 * gateway Thing (its birth: networks, serial ports, config versions) and the
 * driver registry with this gateway's availability. Cache keys match the
 * gateway detail sheet's and the instruments panel's, so one read serves all.
 */

import { useQueries, useQuery } from "@tanstack/react-query";
import { isLiveApi, things, type Thing } from "@/lib/api";
import { gatewayCard, sortGatewayCards, type GatewayCard } from "@/lib/iot/gateway-cards";

/** `GET /v1/things/{id}`: the gateway with its birth (`capabilities`) and `online`. */
export function useGatewayThing(gatewayId: string | null | undefined) {
  return useQuery({
    queryKey: ["thing", gatewayId],
    queryFn: () => things.get(gatewayId!),
    enabled: isLiveApi && !!gatewayId,
    staleTime: 30_000,
  });
}

/**
 * `GET /v1/things/drivers?gateway_id=`: every driver with its schemas and
 * whether a device on this gateway may use it. A server older than the
 * multi-protocol backend has no such route (404); callers then fall back to
 * Modbus TCP only, today's behaviour.
 */
export function useGatewayDrivers(gatewayId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ["thing-drivers", gatewayId ?? null],
    queryFn: () => things.listDrivers(gatewayId ?? undefined),
    enabled: isLiveApi && enabled,
    staleTime: 60_000,
    retry: 1,
  });
}

/** Gateways fetched one by one for their cards (firmware from the birth); more are shown without it. */
const CARD_DETAIL_LIMIT = 12;

/**
 * The Discover step's gateway cards: the things list's gateways, each with its
 * GET (the birth: firmware, config versions; same cache key as
 * {@link useGatewayThing}) and the product catalogue (same key as the gateway
 * detail sheet), sorted online and multi-protocol first.
 */
export function useGatewayCards(rows: Thing[]): { cards: GatewayCard[]; loadingDetails: boolean } {
  const productsQ = useQuery({
    queryKey: ["thing-products"],
    queryFn: () => things.products(),
    enabled: isLiveApi && rows.length > 0,
    staleTime: 5 * 60_000,
  });
  const details = useQueries({
    queries: rows.slice(0, CARD_DETAIL_LIMIT).map((r) => ({
      queryKey: ["thing", r.id],
      queryFn: () => things.get(r.id),
      enabled: isLiveApi,
      staleTime: 30_000,
    })),
  });
  const loadingDetails = details.some((d) => d.isLoading) || productsQ.isLoading;
  const products = productsQ.data?.products;
  // A handful of gateways: cheap enough to build on every render.
  const cards = sortGatewayCards(rows.map((r, i) => gatewayCard(r, details[i]?.data, products)));
  return { cards, loadingDetails };
}
